import VueRouter from 'vue-router';

import { frontendURL } from '../helper/URLHelper';
import dashboard from './dashboard/dashboard.routes';
import store from '../store';
import { validateLoggedInRoutes } from '../helper/routeHelpers';
import AnalyticsHelper from '../helper/AnalyticsHelper';
import { buildPermissionsFromRouter } from '../helper/permissionsHelper';
import { trackEvent } from '../../shared/amplitude';
import { REDIS_FEATURE_FLAGS } from '../featureFlags';

const routes = [...dashboard.routes];

const CLAUDIA_ONLY_PASSTHROUGH_REGEX =
  /\/accounts\/\d+\/(claudia|notifications|profile|settings\/agents|reports\/create-mode)(\/|$)/;
const claudiaLandingFor = id => `accounts/${id}/claudia/agents`;

const CLAUDIA_PERMISSIONS = ['administrator', 'cx_engineer'];

const userCanAccessClaudia = (user, accountId) => {
  const account = (user.accounts || []).find(
    a => Number(a.id) === Number(accountId)
  );
  if (!account) return false;
  const permissions = account.permissions || [];
  return CLAUDIA_PERMISSIONS.some(p => permissions.includes(p));
};

export const router = new VueRouter({ mode: 'history', routes });

router.afterEach((to, from) => {
  trackEvent('page_view', {
    page_name: to.name,
    page_path: to.fullPath,
    from_path: from.fullPath,
  });
});

export const routesWithPermissions = buildPermissionsFromRouter(routes);

export const validateAuthenticateRoutePermission = (to, next, { getters }) => {
  const { isLoggedIn, getCurrentUser: user } = getters;

  if (!isLoggedIn) {
    window.location = '/app/login';
    return '/app/login';
  }

  const isFeatureEnabled = getters['accounts/isFeatureEnabledonAccount'];
  const accountId = Number(to.params.accountId || user.account_id);
  const claudiaOnly =
    typeof isFeatureEnabled === 'function' &&
    isFeatureEnabled(accountId, REDIS_FEATURE_FLAGS.CLAUDIA_ONLY);

  if (claudiaOnly && userCanAccessClaudia(user, accountId)) {
    if (!to.name) {
      return next(frontendURL(claudiaLandingFor(accountId)));
    }
    if (
      to.name !== 'account_suspended' &&
      !CLAUDIA_ONLY_PASSTHROUGH_REGEX.test(to.path)
    ) {
      return next(frontendURL(claudiaLandingFor(accountId)));
    }
    // account_suspended whitelist: pass through only when the account is
    // genuinely suspended; an active user on /suspended is redirected to
    // /claudia/agents (NOT to /dashboard like the legacy chain would).
    if (to.name === 'account_suspended') {
      const currentAccount = (user.accounts || []).find(
        a => Number(a.id) === accountId
      );
      if (currentAccount && currentAccount.status !== 'active') {
        return next();
      }
      return next(frontendURL(claudiaLandingFor(accountId)));
    }
    // /claudia/* — fall through to validateLoggedInRoutes so membership,
    // permissions, and account status are still enforced.
  }

  if (!to.name) {
    return next(frontendURL(`accounts/${user.account_id}/dashboard`));
  }

  const nextRoute = validateLoggedInRoutes(to, getters.getCurrentUser);
  return nextRoute ? next(frontendURL(nextRoute)) : next();
};

export const initalizeRouter = () => {
  // Preload account features (e.g. claudia_only) before the first
  // navigation. setUser only populates currentUser; account features
  // come from accounts/get and are needed by the route guard to decide
  // whether to redirect to /claudia/* on first sign-in. Without this
  // chain, the first navigation races against App.vue#initializeAccount
  // and can land claudia_only users on /dashboard.
  const userAuthentication = store.dispatch('setUser').then(() => {
    if (!store.getters.isLoggedIn) return undefined;
    return store.dispatch('accounts/get').catch(() => {
      // Don't block navigation if accounts fetch fails — features may be
      // stale but rendering anything is better than hanging.
      return undefined;
    });
  });

  router.beforeEach((to, from, next) => {
    AnalyticsHelper.page(to.name || '', {
      path: to.path,
      name: to.name,
    });

    userAuthentication.then(() => {
      return validateAuthenticateRoutePermission(to, next, store);
    });
  });
};

export default router;
